import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, Icon, cn, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import type { PaymentMethodOut } from "../shared";
import { GatedButton } from "./gated-button";
import { MethodMark } from "./method-mark";
import { TestModeNotice } from "./test-mode-notice";
import { useInvalidateBilling } from "./use-billing";

function useMethodLabel() {
  const { t } = useTranslation();
  return (method: PaymentMethodOut) =>
    t(`billing.methods.label_${method.type}`, { brand: method.brand && method.brand !== "card" ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1) : t("billing.methods.type_card"), last4: method.last4 ?? "••••" });
}

function useMethodMutations() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  const makeDefault = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/billing/payment-methods/{method_id}/default", { params: { path: { method_id: id } } })),
    onSuccess: async () => {
      toast.success(t("billing.methods.defaultSet"));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/billing/payment-methods/{method_id}", { params: { path: { method_id: id } } })),
    onSuccess: async () => {
      toast.success(t("billing.methods.removed"));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  return { makeDefault, remove };
}

interface PaymentMethodsCardProps {
  methods: readonly PaymentMethodOut[];
  /** Gateways the workspace may newly pay through (`overview.offered_gateways`, DEC-155). */
  offered: readonly string[];
  canManage: boolean;
  /** Opens the "Add payment method" dialog (PayPal). */
  onAdd: () => void;
  /** Opens the sales request form ("Other payment options: contact sales"). */
  onContactSales: () => void;
}

/** What the payment methods card says when the workspace is billed by invoice: nothing to add or charge. */
function InvoicedBody({ onContactSales }: { onContactSales: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="file-text"
      headingLevel={3}
      title={t("billing.manual.methodsTitle")}
      description={t("billing.manual.methodsBody")}
      action={
        <Button variant="secondary" onClick={onContactSales}>
          <Icon name="mail" />
          {t("billing.manual.contact")}
        </Button>
      }
    />
  );
}

function ContactSalesNote({ onContactSales }: { onContactSales: () => void }) {
  const { t } = useTranslation();
  return (
    <p className="card-body flex flex-wrap items-center gap-x-1 border-t border-line pt-3 text-xs text-fg-muted" data-testid="other-options">
      <Icon name="info" className="size-4 shrink-0" />
      {t("billing.methods.otherOptionsBefore")}{" "}
      <Button variant="ghost" size="sm" className="-mx-1 underline underline-offset-2" onClick={onContactSales}>
        {t("billing.methods.otherOptionsAction")}
      </Button>
    </p>
  );
}

export function PaymentMethodsCard({ methods, offered, canManage, onAdd, onContactSales }: PaymentMethodsCardProps) {
  const { t } = useTranslation();
  const label = useMethodLabel();
  const { makeDefault, remove } = useMethodMutations();
  const sorted = [...methods].sort((a, b) => Number(b.is_default) - Number(a.is_default));
  const canAdd = offered.length > 0;
  return (
    <section className="card" aria-labelledby="pm-title">
      <div className="card-header">
        <h2 id="pm-title" className="card-title">
          {t("billing.methods.title")}
        </h2>
        {canAdd ? (
          <GatedButton allowed={canManage} variant="secondary" size="sm" onClick={onAdd}>
            <Icon name="plus" />
            {t("billing.methods.addPaypal")}
          </GatedButton>
        ) : null}
      </div>
      <TestModeNotice gateways={offered} className="mx-5 mt-4" />
      {sorted.length === 0 ? (
        <EmptyState
          icon="credit-card"
          headingLevel={3}
          title={t("billing.methods.emptyTitle")}
          description={t("billing.methods.emptyBody")}
          action={canManage && canAdd ? <Button onClick={onAdd}><Icon name="plus" />{t("billing.methods.addPaypal")}</Button> : undefined}
        />
      ) : (
        <ul className="card-body space-y-3">
          {sorted.map((method) => (
            <li key={method.id} className={cn("flex items-center gap-3 rounded-md border p-3", method.is_default ? "border-primary bg-primary-soft" : "border-line")}>
              <MethodMark type={method.type} brand={method.brand} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">
                  {label(method)} <span className="text-xs font-normal text-fg-subtle">{t("billing.methods.via", { gateway: t(`billing.gateway.name_${method.provider}`, { defaultValue: method.provider }) })}</span>
                </div>
                <div className="text-xs text-fg-muted">
                  {[
                    method.exp_month && method.exp_year ? t("billing.methods.expires", { date: `${String(method.exp_month).padStart(2, "0")}/${String(method.exp_year)}` }) : "",
                    offered.includes(method.provider) ? "" : t("billing.methods.legacy"),
                    method.is_default ? "" : t("billing.methods.backup"),
                  ]
                    .filter(Boolean)
                    .join(" · ") || t("billing.methods.defaultHint")}
                </div>
              </div>
              {method.is_default ? (
                <Badge tone="brand">{t("billing.methods.default")}</Badge>
              ) : (
                <GatedButton allowed={canManage} variant="ghost" size="sm" loading={makeDefault.isPending && makeDefault.variables === method.id} onClick={() => { makeDefault.mutate(method.id); }}>
                  {t("billing.methods.makeDefaultAction")}
                  <span className="sr-only">: {label(method)}</span>
                </GatedButton>
              )}
              <GatedButton allowed={canManage} variant="ghost" size="sm" iconOnly aria-label={t("billing.methods.remove", { method: label(method) })} loading={remove.isPending && remove.variables === method.id} onClick={() => { remove.mutate(method.id); }}>
                <Icon name="trash" />
              </GatedButton>
            </li>
          ))}
        </ul>
      )}
      <ContactSalesNote onContactSales={onContactSales} />
    </section>
  );
}

/** The payment methods card of a workspace billed by invoice: no methods, no charges. */
export function InvoicedMethodsCard({ onContactSales }: { onContactSales: () => void }) {
  const { t } = useTranslation();
  return (
    <section className="card" aria-labelledby="pm-title">
      <div className="card-header">
        <h2 id="pm-title" className="card-title">
          {t("billing.methods.title")}
        </h2>
      </div>
      <InvoicedBody onContactSales={onContactSales} />
    </section>
  );
}
